import { head, last, sumBy } from 'lodash';
import { WithSizes } from './types';

export const theyFit = <T extends WithSizes>(
  elements: T[],
  maxWidth: number
): boolean => sumBy(elements, 'width') <= maxWidth;

/**
 * Checks if the total width of the elements including the ellipsis is less than the maxWidth.
 * If not, it removes the last element and calls itself recursively.
 */
const trimElements = <T extends WithSizes>(
  elements: T[],
  maxWidth: number,
  ellipsis: T
): T[] => {
  if (elements.length === 0) return [ellipsis];
  const withEllipsis = [...elements, ellipsis];
  if (theyFit(withEllipsis, maxWidth)) return withEllipsis;
  return trimElements(elements.slice(0, -1), maxWidth, ellipsis);
};

/**
 * Distributes the elements in a container with an ellipsis in the middle.
 * The leading and trailing elements are always visible.
 */
export const ellipsisLayout = <T extends WithSizes>(
  elements: T[],
  containerWidth: number,
  ellipsis: T
): (T & WithSizes)[] => {
  const leadingElement = head(elements)!;
  const trailingElement = last(elements)!;
  const body = trimElements(
    elements.slice(1, -1),
    containerWidth - sumBy([leadingElement, trailingElement], 'width'),
    ellipsis
  );
  return [leadingElement, ...body, trailingElement];
};
